import React, { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, NavLink as RouterNavLink, Outlet, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import type { GridApi } from 'ag-grid-community';

import LayoutCompany from 'components/mui/Layouts/Company';
import CompanyNavigation from 'pages/company/components/CompanyNavigation';
import ROUTES_PATH from 'domain/router/routesPathConfig';
import ConnectionHealthAlert from 'components/ConnectionHealthAlert';
import SystemMessages from 'components/SystemMessages';
import type { BankFeedTransactionRow } from 'domain/bankFeed/schemas';
import {
  BANK_FEED_ROUTE_TABS,
  BANK_FEED_TAB_ACCOUNT_TYPES,
  type BankFeedRouteTab,
} from 'pages/company/BankFeedRoute/helper';
import { BankFeedRouteProvider, type BankFeedRouteContext } from 'pages/company/BankFeedRoute/context';
import { deriveCurrencyOptions } from 'pages/company/BankFeedRoute/currencyOptions';
import useBankFeedSummary from 'pages/company/BankFeedRoute/hooks/useBankFeedSummary';
import FilterToolbar from 'pages/company/BankFeedRoute/components/FilterToolbar';
import ExportButton from 'pages/company/BankFeedRoute/components/ExportButton';
import {
  TOOLBAR_FILTERS_INITIAL,
  type ToolbarFilters,
} from 'pages/company/BankFeedRoute/components/FilterToolbar/types';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import LinearProgress from '@mui/material/LinearProgress';
import Tab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';

const isKnownTab = (tab: string): tab is BankFeedRouteTab =>
  tab === BANK_FEED_ROUTE_TABS.TRANSACTIONS || tab === BANK_FEED_ROUTE_TABS.CREDIT_CARDS;

const BankFeedRoute: React.FC = () => {
  const { formatMessage } = useIntl();
  const { companyId = '', '*': asterisk = '' } = useParams();
  const [tabSegment] = asterisk.split('/');
  const activeTab: BankFeedRouteTab = isKnownTab(tabSegment) ? tabSegment : BANK_FEED_ROUTE_TABS.TRANSACTIONS;
  const activeAccountType = BANK_FEED_TAB_ACCOUNT_TYPES[activeTab];

  const { summary, isLoading } = useBankFeedSummary();
  const [toolbarFilters, setToolbarFilters] = useState<ToolbarFilters>(TOOLBAR_FILTERS_INITIAL);
  const gridApiRef = useRef<GridApi<BankFeedTransactionRow> | null>(null);
  const isInitialSummaryLoad = isLoading && summary == null;

  // BE guarantees one summary item per account type — find by accountType.
  const bankItem = summary?.items.find(
    (i) => i.accountType === BANK_FEED_TAB_ACCOUNT_TYPES[BANK_FEED_ROUTE_TABS.TRANSACTIONS],
  );
  const creditCardItem = summary?.items.find(
    (i) => i.accountType === BANK_FEED_TAB_ACCOUNT_TYPES[BANK_FEED_ROUTE_TABS.CREDIT_CARDS],
  );

  // CARD tab account filter is single-value (DA-19483): accountIds carries exactly one card id
  // whenever the tab has cards ([] = "all" stays Bank-only); it stays [] only in the zero-cards
  // empty state (no grid mounts). Derived from the already-resolved creditCardItem so we don't
  // re-scan summary.
  const cardAccountIds = useMemo(() => creditCardItem?.accounts.map((a) => a.id) ?? [], [creditCardItem]);
  const isCardTab = activeAccountType === BANK_FEED_TAB_ACCOUNT_TYPES[BANK_FEED_ROUTE_TABS.CREDIT_CARDS];
  const isCardSelectionValid =
    toolbarFilters.accountIds.length === 1 && cardAccountIds.includes(toolbarFilters.accountIds[0]);

  const defaultCurrencyAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultCurrencyAppliedRef.current) return;
    if (summary == null) return;
    defaultCurrencyAppliedRef.current = true;
    setToolbarFilters((prev) => (prev.currency != null ? prev : { ...prev, currency: summary.defaultCurrency }));
  }, [summary]);

  // DA-19365: currency options derive from the selected accounts' currencies, so changing the
  // "Bank and account" selection (or a defaultCurrency absent from the all-accounts list) can drop
  // the active currency out of range. Keep currency a valid member of the derived list — snap to the
  // first option when it falls out. Initial / tab-switch seeding stays summary.defaultCurrency
  // (Model A, above and in the tab-switch reset); this effect only corrects an out-of-range value,
  // never overrides a still-valid pick, and defers seeding (currency == null) to the effect above.
  const currencyOptions = useMemo(
    () => deriveCurrencyOptions(summary, activeAccountType, toolbarFilters.accountIds),
    [summary, activeAccountType, toolbarFilters.accountIds],
  );
  useEffect(() => {
    if (toolbarFilters.currency == null) return;
    if (currencyOptions.length === 0) return;
    if (currencyOptions.includes(toolbarFilters.currency)) return;
    setToolbarFilters((prev) =>
      prev.currency != null && !currencyOptions.includes(prev.currency)
        ? { ...prev, currency: currencyOptions[0] }
        : prev,
    );
  }, [currencyOptions, toolbarFilters.currency]);

  // DA-19483: the CARD tab's account filter is single-value — exactly one card must always be
  // selected. Seed it to the first card on initial load, and correct it whenever the selection
  // is empty or no longer a valid card. Mirrors the currency snap-to-first effect; the Bank tab
  // keeps accountIds === [] (= all).
  useEffect(() => {
    if (!isCardTab) return;
    if (cardAccountIds.length === 0) return;
    if (isCardSelectionValid) return;
    setToolbarFilters((prev) => ({ ...prev, accountIds: [cardAccountIds[0]] }));
  }, [isCardTab, cardAccountIds, isCardSelectionValid]);

  // Reset toolbar filters to per-tab defaults when the active tab changes. Currency options and
  // account id-spaces are per-accountType (a value picked on one tab is not guaranteed valid on
  // the other), so persisting them across a tab switch can apply an out-of-range filter. Done
  // during render (React's "adjust state when a prop changes" pattern) rather than in an effect
  // so the freshly mounted child grid reads the reset filters on its first render — avoiding a
  // throwaway getRows with the previous tab's currency.
  const [prevAccountType, setPrevAccountType] = useState(activeAccountType);
  if (prevAccountType !== activeAccountType) {
    setPrevAccountType(activeAccountType);
    setToolbarFilters({
      ...TOOLBAR_FILTERS_INITIAL,
      currency: summary?.defaultCurrency ?? null,
      // CARD is single-value: seed the first card (empty until cards load, then the snap effect
      // corrects). Bank stays [] (= all accounts).
      accountIds: isCardTab ? cardAccountIds.slice(0, 1) : [],
    });
  }

  // DA-19485: clear the CARD payment date the instant the (card, currency) pair changes, in the same
  // render the change is observed. This drops the grid's serverSideDatasource (BankFeedGrid gates it
  // on paymentDate) so it shows AG Grid's loading rows instead of fetching with the previous pair's
  // date — the grid stays mounted throughout, so its applied column filters / sort survive (grid
  // state persistence excludes filter/sort by design, so unmounting would lose them). CardDateRange-
  // Filter re-seeds the newest date for the new pair once its options load. Done here (parent owns
  // toolbarFilters) — a child can't setState on its parent during render. Mirrors the tab-reset above.
  const cardKey =
    isCardTab && isCardSelectionValid && toolbarFilters.currency != null
      ? `${toolbarFilters.accountIds[0]}|${toolbarFilters.currency}`
      : null;
  const [prevCardKey, setPrevCardKey] = useState(cardKey);
  if (prevCardKey !== cardKey) {
    setPrevCardKey(cardKey);
    if (cardKey != null) {
      setToolbarFilters((prev) => (prev.paymentDate == null ? prev : { ...prev, paymentDate: null }));
    }
  }

  const contextValue = useMemo<BankFeedRouteContext>(
    () => ({
      summary,
      isSummaryLoading: isLoading,
      toolbarFilters,
      setToolbarFilters,
      accountType: activeAccountType,
      gridApiRef,
    }),
    [summary, isLoading, toolbarFilters, setToolbarFilters, activeAccountType, gridApiRef],
  );

  // Gate grid render on currency being set (applied from summary.defaultCurrency via the
  // ref-based effect above). Without this gate, AG Grid fires getRows once with currency=null
  // and a second time once the default arrives — a race that produces duplicate backend calls
  // and an empty first response. Currency is a required search-payload field. On the CARD tab
  // (DA-19483) also wait for a valid single card so the first getRows never carries
  // account_ids: [] (= all cards) before the single-select seed settles — but only when cards
  // exist; with zero cards the child route renders its EmptyState (no grid), so the Outlet must
  // still mount. CARD payment-date readiness is deliberately NOT gated here (DA-19485): unmounting
  // the grid on every card/currency change discards its applied column filters / sort (grid state
  // persistence excludes filter/sort by design). The grid stays mounted and BankFeedGrid withholds
  // its serverSideDatasource until a payment date seeds — AG Grid shows loading rows meanwhile — then
  // loads once. See BankFeedGrid.
  const isGridReady =
    toolbarFilters.currency != null &&
    (!isCardTab || cardAccountIds.length === 0 || isCardSelectionValid);

  // Tab counts are the company-wide totals from the summary — intentionally filter-independent
  // (grid filters/search/date range never change them, DA-19506). 0 placeholder until summary loads.
  const bankLabel = `${formatMessage({
    id: 'company.bankFeed.transactions.tab.bank',
    defaultMessage: 'Bank Transactions',
  })} (${bankItem?.transactionCount ?? 0})`;
  const creditCardLabel = `${formatMessage({
    id: 'company.bankFeed.transactions.tab.creditCard',
    defaultMessage: 'Credit Card Transactions',
  })} (${creditCardItem?.transactionCount ?? 0})`;

  return (
    <BankFeedRouteProvider value={contextValue}>
      <LayoutCompany
        suppressSearch
        defaultDrawerOpen={false}
        DrawerContent={<CompanyNavigation onDocMove={() => {}} />}
      >
        <ConnectionHealthAlert />
        <Box sx={{ borderBottom: 1, borderColor: 'divider', position: 'relative' }}>
          <Container maxWidth={false}>
            <SystemMessages />
            <MuiTabs
              value={activeTab}
              aria-label={formatMessage({ id: 'company.bankFeed', defaultMessage: 'Bank Feed' })}
            >
              <Tab
                value={BANK_FEED_ROUTE_TABS.TRANSACTIONS}
                component={RouterNavLink}
                to={generatePath(ROUTES_PATH.COMPANY_BANK_FEED_TRANSACTIONS.absolute, { companyId })}
                label={bankLabel}
              />
              <Tab
                value={BANK_FEED_ROUTE_TABS.CREDIT_CARDS}
                component={RouterNavLink}
                to={generatePath(ROUTES_PATH.COMPANY_BANK_FEED_CREDIT_CARDS.absolute, { companyId })}
                label={creditCardLabel}
              />
            </MuiTabs>
          </Container>
          {isInitialSummaryLoad && (
            <LinearProgress
              sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2 }}
              aria-label="Loading bank feed summary"
            />
          )}
        </Box>
        <Container maxWidth={false} sx={{ flexGrow: 1, pt: 0, pb: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FilterToolbar />
            <ExportButton />
          </Box>
          {isGridReady ? <Outlet /> : null}
        </Container>
      </LayoutCompany>
    </BankFeedRouteProvider>
  );
};

export default BankFeedRoute;
